// src/components/NotesList.js

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagProvider';
import { useUIMetrics } from '../hooks/useUIMetrics';
import SignedAudio from './SignedAudio';
import {
  List,
  ListItem,
  ListItemText,
  Divider,
  Typography,
  MenuItem,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  Stack,
  TextField,
  CircularProgress,
  Alert,
  Box,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Autocomplete
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  VolumeUp as VolumeIcon, 
  FolderOpen as ProjectIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { Download as DownloadIcon } from '@mui/icons-material';
import axios from '../api/axiosConfig';
import NoteEditor from './notes/NoteEditor';
import ProjectPickerDialog from './ProjectPickerDialog';
import { useTheme } from '@mui/material/styles';
import { flexRowWrap, dialogTitle } from './common/styles';

const NotesList = () => {
  const { currentUser } = useAuth();
  const { get: getFlag } = useFeatureFlags();
  const { trackCompactMode, trackKeyboard, trackContextMenu, trackViewPerformance, trackViewportDensity } = useUIMetrics();
  const compactMode = true; // Always use compact table view
  const theme = useTheme();
  
  const [notes, setNotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [editNote, setEditNote] = useState(null);
  const [editText, setEditText] = useState('');
  const [editTags, setEditTags] = useState([]);
  const [editTagInput, setEditTagInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingAudio, setProcessingAudio] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [previewNote, setPreviewNote] = useState(null);
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  
  // Compact mode specific states
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [contextMenuAnchor, setContextMenuAnchor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const selectedRowRef = useRef(null);
  const [projects, setProjects] = useState([]);

  const limit = 10;

  // Function to get label colors
  const getLabelColor = (labels) => {
    const colorMap = {
      Todo: theme.palette.info.main,
      Idea: theme.palette.success.main,
      Habit: theme.palette.warning.main,
      Recommendation: theme.palette.secondary.main,
    };

    return labels.map((label) => (
      <Chip
        key={label}
        label={label}
        size="small"
        sx={{
          bgcolor: colorMap[label] || theme.palette.grey[500],
          color: theme.palette.common.white,
          mr: 0.5,
        }}
      />
    ));
  };

  // Load notes from backend
  const loadNotes = async (pageToLoad = 1, reset = false) => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const token = await currentUser.getIdToken();
      const params = { page: pageToLoad, limit };
      if (tagFilter) params.tag = tagFilter;
      const response = await axios.get('/api/notes', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      const fetched = response.data;
      // Filter out voice notes - they should only appear in Voices page
      const textNotes = fetched.filter(note => note.type !== 'voice');
      setNotes(prev => reset ? textNotes : [...prev, ...textNotes]);
      setHasMore(fetched.length === limit);
      setPage(pageToLoad);
      setError('');
    } catch (err) {
      console.error('Error loading notes:', err);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  // Load projects for compact mode
  const loadProjects = async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.get('/api/sessions', {
        headers: { Authorization: `Bearer ${token}` },
        params: { type: 'project' }
      });
      setProjects(response.data);
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  const loadTags = async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.get('/api/bookmarks/tags', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableTags(response.data || []);
    } catch (err) {
      console.error('Error loading tags:', err);
      setAvailableTags([]);
    }
  };

  useEffect(() => {
    loadNotes(1, true);
    if (compactMode) {
      loadProjects();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, tagFilter, compactMode]);

  useEffect(() => {
    loadTags();
  }, [currentUser]);

  // Listen for noteAdded events to auto-refresh
  useEffect(() => {
    const handleNoteAdded = () => {
      loadNotes(1, true);
    };

    window.addEventListener('noteAdded', handleNoteAdded);
    return () => {
      window.removeEventListener('noteAdded', handleNoteAdded);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      const token = await currentUser.getIdToken();
      await axios.delete(`/api/notes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Reload notes after deletion
      loadNotes(1, true);
    } catch (error) {
      console.error('Error deleting note:', error);
      setError('Failed to delete note');
    }
  };

  const handleGenerateAiTags = async (note) => {
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.post(`/api/notes/${note._id}/ai-tags`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const tags = response.data.tags;
      setNotes(prev => prev.map(n => n._id === note._id ? { ...n, metadata: { ...(n.metadata || {}), aiLabels: tags } } : n));
      setError('');
    } catch (err) {
      console.error('Error generating AI tags:', err);
      setError('Failed to generate AI tags');
    }
  };

  const handleEdit = (note) => {
    setEditNote(note);
    // For voice notes, edit the transcript, for text notes edit the content
    setEditText(note.transcript || note.content || note.text || '');
    setEditTags(note.metadata?.labels || []);
    setEditTagInput('');
  };

  // Handle double-click to preview note
  const handlePreview = (note) => {
    setPreviewNote(note);
  };

  const handleAddEditTag = () => {
    const trimmed = editTagInput.trim();
    if (trimmed && !editTags.includes(trimmed)) {
      setEditTags([...editTags, trimmed]);
    }
    setEditTagInput('');
  };

  const handleDeleteEditTag = (tagToDelete) => {
    setEditTags(editTags.filter((t) => t !== tagToDelete));
  };

  const handleDeleteAudio = async (note) => {
    if (!window.confirm('Delete audio file? The transcript will be preserved.')) return;
    
    try {
      setProcessingAudio(prev => ({ ...prev, [note._id]: true }));
      const token = await currentUser.getIdToken();
      await axios.post(`/api/voice/${note._id}/delete-audio`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadNotes(1, true); // Reload to show updated note
    } catch (error) {
      console.error('Error deleting audio:', error);
      setError('Failed to delete audio');
    } finally {
      setProcessingAudio(prev => ({ ...prev, [note._id]: false }));
    }
  };

  // Project assignment handlers
  const handleProjectMenuOpen = async (event, note) => {
    event.stopPropagation();
    setSelectedNote(note);
    setShowProjectPicker(true);
  };

  const handleProjectSelected = async (project) => {
    if (!selectedNote) return;
    
    try {
      const token = await currentUser.getIdToken();
      const projectId = project._id || project.id;
      
      await axios.patch(`/api/notes/${selectedNote._id}/project`, { projectId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[NotesList] Note assigned to project:', project.name);
      setShowProjectPicker(false);
      setSelectedNote(null);
      
      // Refresh notes list to reflect changes
      loadNotes(1, true);
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Failed to assign note to project:', err);
      setError('Failed to assign note to project');
      setShowProjectPicker(false);
      setSelectedNote(null);
    }
  };

  const handleExport = async (format = 'json') => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.get('/api/notes/export', {
        headers: { Authorization: `Bearer ${token}` },
        params: { format },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: format === 'csv' ? 'text/csv' : 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `notes.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting notes:', err);
      setError('Failed to export notes');
    }
  };

  const handleUpdate = async () => {
    if (!editNote) return;

    try {
      const token = await currentUser.getIdToken();
      
      if (editNote.type === 'voice') {
        // Update transcript for voice notes
        await axios.patch(`/api/voice/${editNote._id}/transcript`, {
          transcript: editText
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await axios.patch(`/api/notes/${editNote._id}`, {
          metadata: { labels: editTags }
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Update content for regular notes (enhanced format)
        await axios.patch(`/api/notes/${editNote._id}`, {
          content: editText, // Use enhanced content field
          text: editText, // Keep legacy field for compatibility
          metadata: { labels: editTags }
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setEditNote(null);
      setEditText('');
      setEditTags([]);
      loadNotes(1, true); // Reload notes
    } catch (error) {
      console.error('Error updating note:', error);
      setError('Failed to update note');
    }
  };

  const filteredNotes = notes.filter((note) => {
    // Search across all text fields including enhanced fields
    const searchText = (
      (note.title || '') + ' ' +
      (note.content || note.text || '') + ' ' +
      (note.transcript || '') + ' ' +
      (note.excerpt || '')
    ).toLowerCase();
    const matchesSearch = searchText.includes(searchTerm.toLowerCase());
    const matchesTag = !tagFilter || (note.metadata?.labels || []).includes(tagFilter);
    return matchesSearch && matchesTag;
  });

  // Track compact mode usage on mount
  useEffect(() => {
    if (compactMode) {
      trackCompactMode('notes_list', true, 'view');
      // Track viewport density
      const viewportHeight = window.innerHeight;
      const estimatedVisibleRows = Math.floor(viewportHeight / 56); // 56px row height
      trackViewportDensity('notes_list', 'compact', Math.min(estimatedVisibleRows, filteredNotes.length));
    }
  }, [compactMode, trackCompactMode, trackViewportDensity, filteredNotes.length]);

  // Keyboard navigation for compact mode
  useEffect(() => {
    if (!compactMode) return;
    
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = Math.min(prev + 1, filteredNotes.length - 1);
          trackKeyboard('notes_table', 'ArrowDown', newIndex !== prev);
          return newIndex;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = Math.max(prev - 1, 0);
          trackKeyboard('notes_table', 'ArrowUp', newIndex !== prev);
          return newIndex;
        });
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        trackKeyboard('notes_table', 'Enter', true);
        handlePreview(filteredNotes[selectedIndex]);
      } else if (e.key === 'Delete' && selectedIndex >= 0) {
        e.preventDefault();
        trackKeyboard('notes_table', 'Delete', true);
        setConfirmDelete(filteredNotes[selectedIndex]);
      } else if (e.key === 'Escape') {
        trackKeyboard('notes_table', 'Escape', true);
        setContextMenuAnchor(null);
        setConfirmDelete(null);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [compactMode, selectedIndex, filteredNotes]);

  // Context menu handlers for compact mode
  const handleCloseContextMenu = () => {
    setContextMenuAnchor(null);
  };

  const handleMoveToProject = () => {
    if (selectedIndex >= 0) {
      setSelectedNote(filteredNotes[selectedIndex]);
      setShowProjectPicker(true);
    }
  };

  const formatRelativeTime = (date) => {
    const now = new Date();
    const noteDate = new Date(date);
    const diffInSeconds = Math.floor((now - noteDate) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return noteDate.toLocaleDateString();
  };

  // Utility to strip HTML tags - memoized for performance
  const toPlain = useCallback((s = '') => s.replace(/<[^>]*>/g, ''), []);

  const getFirstLine = useCallback((note) => {
    // Markdown-aware: prefer markdown, then content, then text, then transcript
    const raw = note.markdown ?? note.content ?? note.text ?? note.transcript ?? '';
    const plain = toPlain(raw);
    const firstLine = plain.split('\n')[0]?.trim();
    return firstLine || '';
  }, [toPlain]);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Your Notes
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filter by Tag */}
      {availableTags.length > 0 ? (
        <Autocomplete
          freeSolo
          options={availableTags}
          value={tagFilter}
          onChange={(e, newValue) => setTagFilter(newValue || '')}
          onInputChange={(e, newInputValue) => setTagFilter(newInputValue)}
          renderInput={(params) => (
            <TextField {...params} label="Filter by Tag" margin="normal" fullWidth />
          )}
        />
      ) : (
        <TextField
          label="Filter by Tag"
          variant="outlined"
          fullWidth
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          margin="normal"
        />
      )}

      {/* Search Notes */}
        <TextField
          label="Search Notes"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          margin="normal"
        />

        <Stack direction="row" spacing={2} sx={{ my: 2 }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('json')}
          >
            Export JSON
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('csv')}
          >
            Export CSV
          </Button>
        </Stack>

        {loading ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredNotes.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            No notes found.
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 2, border: 1, borderColor: 'divider' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', width: theme.spacing(15) }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', width: theme.spacing(18.75) }}>Tags</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', width: theme.spacing(12.5) }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', width: theme.spacing(7.5) }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredNotes.map((note, index) => (
                  <TableRow
                    key={note._id}
                    ref={index === selectedIndex ? selectedRowRef : null}
                    hover
                    selected={index === selectedIndex}
                    onClick={() => setSelectedIndex(index)}
                    onDoubleClick={() => handlePreview(note)}
                    sx={{
                      cursor: 'pointer',
                      height: theme.spacing(7),
                      '& .MuiTableCell-root': {
                        borderBottom: 1,
                        borderColor: 'divider',
                        py: 1,
                        fontSize: '0.875rem'
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            maxWidth: theme.spacing(37.5),
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {note.title?.trim() || getFirstLine(note) || 'Untitled'}
                        </Typography>
                        {note.title && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              maxWidth: theme.spacing(37.5),
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {getFirstLine(note)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {note.projectId && (
                        <Chip
                          label={Array.isArray(projects) ? projects.find(p => p._id === note.projectId)?.title || 'Project' : 'Project'}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={flexRowWrap(theme)}>
                        {note.metadata?.labels?.slice(0, 2).map(tag => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: theme.spacing(2.5) }}
                          />
                        ))}
                        {note.metadata?.labels?.length > 2 && (
                          <Chip
                            label={`+${note.metadata.labels.length - 2}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: theme.spacing(2.5) }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatRelativeTime(note.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedIndex(index);
                          setContextMenuAnchor(e.currentTarget);
                        }}
                        sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

      {hasMore && !loading && (
        <Button variant="contained" fullWidth sx={{ mt: 2 }} onClick={() => loadNotes(page + 1)}>
          Load More
        </Button>
      )}

      {/* Edit Note Dialog - Now using Rich Text Editor */}
      <Dialog 
        open={!!editNote} 
        onClose={() => { setEditNote(null); setEditTags([]); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {editNote && (
            <NoteEditor
              noteId={editNote._id}
              initialTitle={editNote.title || editNote.category || ''}
              initialContent={editNote.content || editNote.text || ''}
              initialFormat={editNote.format || 'markdown'}
              onSave={(savedNote) => {
                setEditNote(null);
                setEditTags([]);
                loadNotes(1, true); // Reload notes list
              }}
              onCancel={() => {
                setEditNote(null);
                setEditTags([]);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Note Dialog - Double-click to open */}
      <Dialog 
        open={!!previewNote} 
        onClose={() => setPreviewNote(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '70vh' }
        }}
      >
        <DialogTitle sx={dialogTitle}>
          <Typography variant="h5" component="span">
            {previewNote?.title || 'Note Preview'}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              handleEdit(previewNote);
              setPreviewNote(null);
            }}
          >
            Edit Note
          </Button>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto' }}>
          {previewNote && (
            <Box>
              {/* Render content based on format */}
              {previewNote.format === 'richtext' && previewNote.contentHtml ? (
                <Box
                  dangerouslySetInnerHTML={{ __html: previewNote.contentHtml }}
                  sx={{
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto'
                    },
                    '& h1, & h2, & h3': {
                      marginTop: '1rem',
                      marginBottom: '0.5rem'
                    },
                    '& p': {
                      marginBottom: '0.75rem'
                    },
                    '& ul, & ol': {
                      marginBottom: '0.75rem',
                      paddingLeft: '1.5rem'
                    }
                  }}
                />
              ) : (
                <Typography 
                  variant="body1" 
                  component="div"
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    fontFamily: previewNote.content?.includes('#') || previewNote.content?.includes('*') ? 'monospace' : 'inherit'
                  }}
                >
                  {previewNote.transcript || previewNote.content || previewNote.text || 'No content'}
                </Typography>
              )}
              
              {/* Show metadata */}
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Typography variant="caption" color="text.secondary">
                    Created: {new Date(previewNote.createdAt).toLocaleString()}
                  </Typography>
                  {previewNote.metadata?.labels && previewNote.metadata.labels.length > 0 && (
                    <Stack direction="row" spacing={0.5}>
                      {previewNote.metadata.labels.map((label) => (
                        <Chip key={label} label={label} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewNote(null)}>
            Close
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              handleEdit(previewNote);
              setPreviewNote(null);
            }}
          >
            Edit Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu for Compact Mode */}
      {compactMode && (
        <Menu
          anchorEl={contextMenuAnchor}
          open={Boolean(contextMenuAnchor)}
          onClose={handleCloseContextMenu}
          PaperProps={{
            sx: { minWidth: 160 }
          }}
        >
          <MenuItem onClick={() => {
            if (selectedIndex >= 0) {
              trackContextMenu('notes_table', 'edit');
              handleEdit(filteredNotes[selectedIndex]);
              handleCloseContextMenu();
            }
          }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={() => {
            trackContextMenu('notes_table', 'move_to_project');
            handleMoveToProject();
            handleCloseContextMenu();
          }}>
            <FolderIcon fontSize="small" sx={{ mr: 1 }} />
            Move to Project
          </MenuItem>
          <MenuItem 
            onClick={() => {
              if (selectedIndex >= 0) {
                trackContextMenu('notes_table', 'delete');
                handleDelete(filteredNotes[selectedIndex]._id);
                handleCloseContextMenu();
              }
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>
      )}

      {/* Project Picker Dialog */}
      <ProjectPickerDialog
        open={showProjectPicker}
        onClose={() => {
          setShowProjectPicker(false);
          setSelectedNote(null);
        }}
        onSelect={handleProjectSelected}
        title="Add Note to Project"
        allowCreate={true}
      />
    </Box>
  );
};

export default NotesList;
